import type { GlobalFilter, FilterType, QueryFormatFilter } from '@hotjar/global-filters';
import type { SiteId } from '../site/model';

export type EffectivePlan = string | null;

export interface QuantzRequest {
  sessionFilters: GlobalFilter;
  siteId?: SiteId;
  operation: QuantzOperation;
  groupBy?: QuantzProperty;
  eventFilters?: QueryFormatFilter;
  limit?: number;
  windowed?: boolean;
  eventType?: string;
  property?: QuantzProperty;
  level?: QuantzLevel;
}

export interface QuantzFunnelsRequest {
  sessionFilters: GlobalFilter;
  steps?: FilterType[];
  siteId?: SiteId;
  windowed?: boolean;
  operations: QuantzOperation[];
}

export enum QuantzOperation {
  COUNT = 'count',
  AVERAGE = 'average',
  MIN = 'min',
  MAX = 'max',
  SESSION_COUNT = 'session_count',
  TIME_AVERAGE = 'time_average',
  CUMULATIVE_TIME_AVERAGE = 'cumulative_time_average',
}

export enum QuantzLevel {
  SESSION = 'session',
  PAGE = 'page',
  EVENT = 'event',
}

export enum QuantzResolution {
  HOUR = 'hour',
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
}

export enum QuantzProperty {
  BROWSER = 'browser',
  COUNTRY_CODE = 'country_code',
  DEVICE = 'device',
  FEEDBACK_SCORE = 'feedback_score',
  IS_FIRST_SEEN = 'is_first_seen',
  OS = 'os',
  PAGE_COUNT = 'page_count',
  PAGE_URL = 'page_url',
  LANDING_PAGE_URL = 'landing_page_url',
  EXIT_PAGE_URL = 'exit_page_url',
  EVENT_NAME = 'name',
  DURATION = 'duration',
  ACTIVE_TIME = 'active_time',
  IS_BOUNCE = 'is_bounce',
  TEXT = 'text',
  PAGE_VISIT_DURATION = 'page_visit_duration',
  DROP_OFF = 'is_dropoff',
}

export enum QuantzEventType {
  AUTOTAG_RECORDING = 'autotag_recording',
  MOUSE_CLICK = 'mouse_click',
  ERROR = 'error',
  PAGE_VISIT = 'page_visit',
}

export enum QuantzQueryCacheDuration {
  Tiny = 3_000, // 3 seconds
  Small = 300_000, // 5 minutes
  Mid = 900_000, // 15 minutes
}

export interface QueryResponse {
  data: null;
}

export interface QuantzMeta<T extends boolean> {
  operation: QuantzOperation;
  operations?: QuantzOperation[];
  success: T;
  windowed: boolean;
  level: QuantzLevel;
  resolution?: QuantzResolution;
}

export interface QuantzData<T extends QuantzDataItem> {
  result?: number;
  resultSeries?: QuantzSeries<T>[];
  total?: number;
  totalSeries?: T[];
}

export interface FunnelStep {
  sessionCount: number;
  timeAverage?: number;
  cumulativeTimeAverage?: number;
}

export interface StepDataByOperation {
  key: QuantzOperation;
  values: QuantzDateDataItem[];
}

type FunnelStepSeries = StepDataByOperation[];

interface QuantzFunnelsWindowedData {
  stepsSeries: FunnelStepSeries[];
}

export interface QuantzFunnelsData {
  steps: FunnelStep[];
}

export enum QuantzResponseKind {
  BY_DATE = 'BY_DATE',
  BY_CATEGORY = 'BY_CATEGORY',
}

export const OTHER_CATEGORY_KEY = 'other';

export type QuantzResponse = QuantzByDateResponse | QuantzByCategoryResponse;

export interface QuantzByDateResponse {
  kind: QuantzResponseKind.BY_DATE;
  meta: QuantzMeta<true>;
  data: QuantzData<QuantzDateDataItem>;
}

export interface QuantzByCategoryResponse {
  kind: QuantzResponseKind.BY_CATEGORY;
  meta: QuantzMeta<true>;
  data: QuantzData<QuantzCategoryDataItem>;
}

export type QuantzFunnelsResponse = QuantzFunnelsStepsResponse | QuantzFunnelsWindowedResponse;

export interface QuantzFunnelsStepsResponse {
  kind: QuantzResponseKind.BY_CATEGORY;
  meta: QuantzMeta<true>;
  data: QuantzFunnelsData;
}

export interface QuantzFunnelsWindowedResponse {
  kind: QuantzResponseKind.BY_DATE;
  meta: QuantzMeta<true>;
  data: QuantzFunnelsWindowedData;
}

export interface QuantzSeries<T extends QuantzDataItem> {
  key: string;
  values: T[];
}

type QuantzDataItem = QuantzDateDataItem | QuantzCategoryDataItem;

export interface QuantzDateDataItem {
  date: Date;
  value: number | null;
}

export interface QuantzCategoryDataItem {
  category: string;
  value: number;
}
